// Bot Protection Utility
// Detects and blocks suspicious bot traffic

/**
 * Check if user agent is a known bot/crawler
 * @returns {boolean} true if bot detected
 */
export const isBot = () => {
  if (typeof window === 'undefined') return false;
  
  const botPatterns = [
    /bot/i,
    /crawler/i,
    /spider/i,
    /scraper/i,
    /curl/i,
    /wget/i,
    /python-requests/i,
    /headless/i,
    /selenium/i,
    /puppeteer/i,
    /playwright/i,
    /phantomjs/i,
    /slimerjs/i,
    /zombie/i,
    /apache-httpclient/i,
    /java/i,
    /scrapy/i,
  ];
  
  const userAgent = navigator.userAgent || '';
  return botPatterns.some(pattern => pattern.test(userAgent));
};

/**
 * Check if running in headless browser
 * @returns {boolean} true if headless detected
 */
export const isHeadless = () => {
  if (typeof window === 'undefined') return false;
  
  const indicators = [
    window.outerWidth === 0 && window.outerHeight === 0,
    window.chrome?.runtime?.OnInstalledReason === undefined && navigator.webdriver,
    navigator.languages?.length === 0,
    window.navigator?.plugins?.length === 0,
    !window.navigator?.mimeTypes?.length,
    /HeadlessChrome/.test(navigator.userAgent),
  ];
  
  return indicators.filter(Boolean).length >= 3;
};

/**
 * Check if session is suspicious
 * @returns {boolean} true if suspicious
 */
export const isSuspiciousSession = () => {
  if (typeof window === 'undefined') return false;
  
  // Check for very fast page loads (bot behavior)
  const navigationTiming = performance.getEntriesByType('navigation')[0];
  if (navigationTiming) {
    const loadTime = navigationTiming.loadEventEnd - navigationTiming.startTime;
    // Too fast = probably a bot (less than 100ms)
    if (loadTime < 100) return true;
  }
  
  // Check for missing typical user interactions
  let hasMouseMovement = false;
  let hasScroll = false;
  
  const checkInteractions = () => {
    hasMouseMovement = true;
    document.removeEventListener('mousemove', checkInteractions);
  };
  
  const checkScroll = () => {
    hasScroll = true;
    document.removeEventListener('scroll', checkScroll);
  };
  
  document.addEventListener('mousemove', checkInteractions, { once: true });
  document.addEventListener('scroll', checkScroll, { once: true });
  
  // After 3 seconds, check if there were any interactions
  setTimeout(() => {
    if (!hasMouseMovement && !hasScroll && window.innerWidth > 768) {
      // Desktop without mouse movement is suspicious
      console.warn('[Bot Protection] No user interactions detected');
    }
  }, 3000);
  
  return false;
};

/**
 * Rate limiter for actions
 */
class ActionRateLimiter {
  constructor() {
    this.actions = new Map();
    this.maxAttempts = 5;
    this.windowMs = 60000; // 1 minute
  }
  
  canPerform(actionId) {
    const now = Date.now();
    const attempts = this.actions.get(actionId) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }
    
    recentAttempts.push(now);
    this.actions.set(actionId, recentAttempts);
    return true;
  }
  
  reset(actionId) {
    this.actions.delete(actionId);
  }
}

export const actionLimiter = new ActionRateLimiter();

/**
 * Block pixel tracking for bots
 * @returns {boolean} true if should block tracking
 */
export const shouldBlockPixelTracking = () => {
  // Block tracking for known bots to prevent fake events
  if (isBot()) {
    console.log('[Bot Protection] Blocking pixel tracking for bot');
    return true;
  }
  
  if (isHeadless()) {
    console.log('[Bot Protection] Blocking pixel tracking for headless browser');
    return true;
  }
  
  return false;
};

/**
 * Initialize bot protection
 */
export const initBotProtection = () => {
  if (typeof window === 'undefined') return;
  
  // Log detection for debugging
  if (isBot()) {
    console.warn('[Bot Protection] Bot detected:', navigator.userAgent);
  }
  
  if (isHeadless()) {
    console.warn('[Bot Protection] Headless browser detected');
  }
  
  // Add data attribute to body for CSS targeting
  if (isBot() || isHeadless()) {
    document.body.setAttribute('data-is-bot', 'true');
  }
};

export default {
  isBot,
  isHeadless,
  isSuspiciousSession,
  actionLimiter,
  shouldBlockPixelTracking,
  initBotProtection,
};
